import {
  FilterChangedEvent,
  GridApi,
  type GridOptions,
  GridReadyEvent,
  ModuleRegistry,
  type RowHeightParams,
} from 'ag-grid-community'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import { ReactNode, useCallback, useMemo } from 'react'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { AgDataTable } from '@/components/lemmala-design-system/table'
import type { AgDataTableProps } from '@/components/lemmala-design-system/table/ag-data-table'
import {
  DEFAULT_GROUP_TABLE_HEIGHT,
  DETAIL_GRID_ROW_HEIGHT,
  FALLBACK_DETAIL_HEIGHT,
  MASTER_ROW_HEIGHT,
  MAX_DETAIL_PANEL_HEIGHT,
  TABLE_HEADER_HEIGHTS,
} from '@/components/lemmala-design-system/table/consts'
import { usePersistedGrouped } from '@/components/lemmala-design-system/table/hooks/use-persisted-grouped'
import { TABLE_LABELS } from '@/components/lemmala-design-system/table/labels'
import type {
  ColumnConfig,
  FilterConfig,
  GroupConfig,
  RowClickMeta,
  RowConfig,
  SearchConfig,
} from '@/components/lemmala-design-system/table/types'
import {
  createAgGridTheme,
  getDefaultColDef,
  mergeGridOptions,
} from '@/components/lemmala-design-system/table/utils/grid-theme'

ModuleRegistry.registerModules([AllEnterpriseModule])

export interface AgGroupTableProps<TData, TMasterRow extends Record<string, unknown>> {
  data: TData[]
  isLoading?: boolean
  tableHeaderActions?: ReactNode
  onRowClick?: (row: TData, meta: RowClickMeta) => void
  filterContext?: string
  groupConfig: GroupConfig<TData, TMasterRow>
  columnConfig: ColumnConfig<TMasterRow>
  filterConfig: FilterConfig<TMasterRow>
  searchConfig: SearchConfig<TData, TMasterRow>
  rowConfig: RowConfig<TMasterRow>
  className?: string
  tableClassName?: string
  suppressColumnHeaders?: boolean
  customFilterBarLeft?: ReactNode
  entityName?: string
  onGridReady?: (api: GridApi<TMasterRow>) => void
  /** Controlled grouping state; omit both to let this component own it. */
  grouped?: boolean
  onGroupedChange?: (grouped: boolean) => void
  containerClassName?: string
  /** Size the grid to its content (autoHeight) instead of filling a fixed height. */
  hug?: boolean
  hideHeader?: boolean
  /** Merged last into the grid options this wrapper builds, for options it does not model. */
  additionalGridOptions?: Partial<GridOptions<TMasterRow>>
  /** The panel is a separate grid, so its interactions are wired here, not on the master grid. */
  additionalDetailGridOptions?: Partial<GridOptions<TMasterRow>>
  /** Escape hatch for AgDataTable features this wrapper does not model. Spread last, so it wins. */
  additionalTableProps?: Partial<
    Omit<
      AgDataTableProps<TMasterRow>,
      | 'rowData'
      | 'columnDefs'
      | 'gridOptions'
      | 'onRowClick'
      | 'onGridReady'
      | 'search'
      | 'filters'
      | 'className'
      | 'tableClassName'
      | 'containerClassName'
    >
  >
}

export const AgGroupTable = <TData extends Record<string, unknown>, TMasterRow extends TData>({
  data,
  isLoading = false,
  tableHeaderActions,
  onRowClick,
  filterContext = 'table',
  groupConfig,
  columnConfig,
  filterConfig,
  searchConfig,
  rowConfig,
  className,
  tableClassName = '',
  suppressColumnHeaders = false,
  customFilterBarLeft,
  onGridReady,
  entityName,
  grouped,
  onGroupedChange,
  containerClassName,
  hug = false,
  hideHeader = false,
  additionalGridOptions,
  additionalDetailGridOptions,
  additionalTableProps,
}: AgGroupTableProps<TData, TMasterRow>) => {
  const isControlled = grouped !== undefined
  const [uncontrolledGrouped, setUncontrolledGrouped] = usePersistedGrouped({
    prefix: filterContext,
    defaultGrouped: groupConfig.defaultGrouped ?? true,
    enabled: !isControlled && !!groupConfig.groupToggleLabel,
  })
  const groupingEnabled = grouped ?? uncontrolledGrouped

  const handleGroupingChange = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledGrouped(next)
      onGroupedChange?.(next)
    },
    [isControlled, setUncontrolledGrouped, onGroupedChange],
  )

  const processedData = useMemo<TMasterRow[]>(() => {
    if (!groupingEnabled && groupConfig.ungroupData) {
      return groupConfig.ungroupData(data)
    }
    return groupConfig.groupData(data, new Set())
  }, [data, groupingEnabled, groupConfig])

  const masterColumnDefs = useMemo(
    () => columnConfig.masterColumns(groupingEnabled),
    [columnConfig, groupingEnabled],
  )

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        ...mergeGridOptions<TMasterRow>({
          theme: createAgGridTheme(),
          suppressRowTransform: true,
          headerHeight: TABLE_HEADER_HEIGHTS.group,
          getRowHeight: () => DETAIL_GRID_ROW_HEIGHT,
          getRowClass: () => rowConfig.getRowClass({ isGroupedDetail: true }),
          // Not 'autoHeight': it cannot virtualise, so it renders every child row. getRowHeight sizes
          // the panel instead.
          domLayout: 'normal' as const,
        }),
        columnDefs: columnConfig.detailColumns,
        defaultColDef: getDefaultColDef<TMasterRow>(),
        ...additionalDetailGridOptions,
      },
      getDetailRowData: (params: {
        data: TMasterRow
        successCallback: (rowData: TMasterRow[]) => void
      }) => {
        const masterRow = params.data

        if (!groupConfig.isMasterRow(masterRow)) {
          params.successCallback([])
          return
        }

        const detailRows = groupConfig.getDetailRows(masterRow)
        params.successCallback(detailRows)
      },
    }),
    [columnConfig.detailColumns, groupConfig, rowConfig, additionalDetailGridOptions],
  )

  const gridOptions = useMemo(
    () => ({
      suppressRowTransform: true,
      suppressGroupRowsSticky: true,
      suppressColumnHeaders,
      headerHeight: suppressColumnHeaders ? 0 : undefined,
      masterDetail: groupingEnabled,
      detailCellRendererParams: groupingEnabled ? detailCellRendererParams : undefined,
      // Would need an autoHeight detail grid; getRowHeight sizes the panel instead.
      detailRowAutoHeight: false,
      isRowMaster: groupingEnabled ? groupConfig.isMasterRow : undefined,
      getRowHeight: (params: RowHeightParams<TMasterRow>) => {
        if (groupingEnabled && params.node.detail === true) {
          const masterRow = params.node.parent?.data
          if (!masterRow) {
            return FALLBACK_DETAIL_HEIGHT
          }

          const detailRows = groupConfig.getDetailRows(masterRow)
          const detailCount = detailRows.length
          // Capped, so a big group scrolls inside its panel.
          return Math.min(
            TABLE_HEADER_HEIGHTS.group + detailCount * DETAIL_GRID_ROW_HEIGHT,
            MAX_DETAIL_PANEL_HEIGHT,
          )
        }
        return MASTER_ROW_HEIGHT
      },
      getRowClass: (params: { data?: TMasterRow }) => rowConfig.getRowClass(params.data),
    }),
    [groupingEnabled, detailCellRendererParams, groupConfig, rowConfig, suppressColumnHeaders],
  )

  const mergedGridOptions = useMemo(
    () => ({
      ...gridOptions,
      onFilterChanged: (params: FilterChangedEvent<TMasterRow>) => {
        filterConfig.onFilterChanged?.(params)
      },
      onGridReady: onGridReady
        ? (event: GridReadyEvent<TMasterRow>) => {
            onGridReady(event.api)
          }
        : undefined,
      ...additionalGridOptions,
    }),
    [gridOptions, filterConfig, onGridReady, additionalGridOptions],
  )

  const doesRowMatch = useCallback(
    (row: TMasterRow, query: string): boolean => searchConfig.doesRowMatch(row, query, data),
    [searchConfig, data],
  )

  const handleRowClick = useCallback(
    (row: TMasterRow, meta: RowClickMeta) => {
      if (!onRowClick) return

      const shouldHandle = rowConfig.shouldHandleRowClick?.(row) ?? true
      if (!shouldHandle) return

      onRowClick(row, meta)
    },
    [onRowClick, rowConfig],
  )

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='text-main-foreground-tertiary'>{TABLE_LABELS.loading}</div>
      </div>
    )
  }

  const groupToggle = groupConfig.groupToggleLabel ? (
    <CheckboxWithLabel
      checked={groupingEnabled}
      onCheckedChange={(checked) => handleGroupingChange(!!checked)}
    >
      {groupConfig.groupToggleLabel}
    </CheckboxWithLabel>
  ) : null

  return (
    <AgDataTable<TMasterRow>
      domLayout={hug ? 'autoHeight' : 'normal'}
      hideHeader={hideHeader}
      rowData={processedData}
      columnDefs={masterColumnDefs}
      gridOptions={mergedGridOptions}
      className={`ag-group-table ${suppressColumnHeaders ? 'ag-group-no-header' : ''} ${className || ''}`}
      tableClassName={tableClassName}
      search={{
        doesRowMatch,
      }}
      urlSync={{
        enabled: true,
        prefix: filterContext,
      }}
      hideExport
      onRowClick={handleRowClick}
      filters={{
        defaultVisibleFilters: filterConfig.defaultVisibleFilters,
      }}
      customFilterBarLeft={customFilterBarLeft || groupToggle}
      headerRight={tableHeaderActions}
      entityName={entityName}
      containerClassName={
        hug ? containerClassName : (containerClassName ?? DEFAULT_GROUP_TABLE_HEIGHT)
      }
      {...additionalTableProps}
    />
  )
}
